import { Modal, Button } from '@distribusion/owl-frontend-ui'
import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { useDeleteAccount } from '@loaders/deleteAccount'
import { DeleteAccountErrorReason } from '@pages/Account/hooks/useDeleteAccount'

interface DeleteAccountModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onError: (reason: DeleteAccountErrorReason) => void
}

export const DeleteAccountModal = ({ isOpen, onClose, onSuccess, onError }: DeleteAccountModalProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateAsync: deleteAccount, isLoading } = useDeleteAccount()

  const handleDelete = async () => {
    try {
      await deleteAccount()
      onSuccess()
      navigate(`/account/account-deleted?${searchParams}&deleted=true`, {
        replace: true,
      })
    } catch (error: any) {
      let errorReason: DeleteAccountErrorReason = 'default'

      if (error.message) {
        switch (error.message) {
          case 'activeSubscriptions':
            errorReason = 'cancellation-required'
            break
          case 'unpaidInvoices':
            errorReason = 'payment-required'
            break
          default:
            errorReason = 'default'
        }
      }

      onError(errorReason)
    }
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={t('account.settings.delete.modal.title')}
      maxWidth="sm"
      dataTag="delete-account-modal"
    >
      <div className={bem('reset-password-modal', 'content')}>
        <span>{t('account.settings.delete.modal.description')}</span>
        <div className={bem('reset-password-modal', 'actions')}>
          <Button
            color="primary"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            dataTag="cancel-delete-account"
          >
            {t('account.settings.delete.modal.keepButton')}
          </Button>
          <Button
            color="primary"
            onClick={handleDelete}
            disabled={isLoading}
            loading={isLoading}
            dataTag="confirm-delete-account"
          >
            {t('account.settings.delete.modal.deleteButton')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
